import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useTranslation } from 'react-i18next';
import { BAROMETER_AREA_ORDER } from 'constants/surveys';
import type { PatientSurveyHistoryItem } from 'types/surveys';
import { formatUnifiedForDisplay } from 'utils/date';
import { getMatchedAnswers } from './utils';

interface TestDetailExpandedProps {
  test: PatientSurveyHistoryItem;
}

export const TestDetailExpanded = ({ test }: TestDetailExpandedProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.PSYCHOLOGIST);
  const {
    completedAt,
    totalSurveyScore,
    maxSurveyScore,
    scorePerTag,
    maxScorePerTag,
    id: testId,
  } = test;

  const matchedAnswers = getMatchedAnswers(test);
  const hasTotalScore = totalSurveyScore !== null && totalSurveyScore !== undefined;
  const areasWithScore = scorePerTag
    ? BAROMETER_AREA_ORDER.filter(areaKey => typeof scorePerTag[areaKey] === 'number')
    : [];

  return (
    <>
      <div className="flex flex-col gap-4">
        {completedAt && (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">
              {t('PsychologistPatients.PatientProfile.Tests.completedAt.label')}
            </p>
            <p className="text-sm text-gray-600">
              {formatUnifiedForDisplay(completedAt, 'dd/MM/yyyy HH:mm')}
            </p>
          </div>
        )}
        {hasTotalScore && (
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">
              {t('PsychologistPatients.PatientProfile.Tests.score.label')}
            </p>
            <p className="text-sm text-gray-600">
              {t('PsychologistPatients.PatientProfile.Tests.scoreOutOf', {
                score: totalSurveyScore,
                max: maxSurveyScore,
              })}
            </p>
          </div>
        )}
        {areasWithScore.length > 0 && (
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">
              {t('PsychologistPatients.PatientProfile.Tests.scoreByArea')}
            </p>
            <ul className="text-sm text-gray-600 flex flex-col gap-0.5 list-none">
              {areasWithScore.map(areaKey => (
                <li key={areaKey} className="flex items-baseline gap-2">
                  <span>
                    {t(`BarometerResults.Areas.${areaKey}`, { ns: LOCIZE_NS.WELL_BEING })}
                  </span>
                  <span className="text-gray-600 font-medium tabular-nums">
                    {t('PsychologistPatients.PatientProfile.Tests.scoreOutOf', {
                      score: scorePerTag?.[areaKey],
                      max: maxScorePerTag?.[areaKey] ?? '-',
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {matchedAnswers.length > 0 ? (
          <div className="flex flex-col gap-4 mt-4">
            <p className="text-sm font-medium">
              {t('PsychologistPatients.PatientProfile.Tests.answers.label')}
            </p>
            <div className="flex flex-col gap-3">
              {matchedAnswers.map((matchedAnswer, index) => (
                <div key={`${testId}-${index}`} className="border-b pb-3 last:border-0">
                  <p className="text-xs text-gray-600 mb-1">
                    {t('PsychologistPatients.PatientProfile.Tests.question.label')} {index + 1}
                  </p>
                  <p className="text-sm font-medium mb-2">{matchedAnswer.question}</p>
                  <p className="text-sm text-gray-600">{matchedAnswer.answers.join(', ')}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            {t('PsychologistPatients.PatientProfile.Tests.noAnswers')}
          </p>
        )}
      </div>
    </>
  );
};
